import React from 'react';
import {
    McCard,
    McButton,
    McIcon,
    McLoadingIndicator,
    McNotification,
} from '@maersk-global/mds-react-wrapper';

interface ExplainPlanCardProps {
    planLines: string[];
    isExplaining?: boolean;
    aiExplanation?: string | null;
    isGeneratingAiExplanation?: boolean;
    validationErrors?: string[];
    onAiExplain?: () => void;
    onClose?: () => void;
    aiModelOptions?: { label: string; value: string }[];
    selectedModel?: string;
    onModelChange?: (model: string) => void;
    onAutoFix?: () => void;
    isAutoFixing?: boolean;
    autoFixModel?: string;
    autoFixModelOptions?: { label: string; value: string }[];
    onAutoFixModelChange?: (model: string) => void;
    unfixableReason?: string;
}

export const ExplainPlanCard: React.FC<ExplainPlanCardProps> = ({
    planLines,
    isExplaining = false,
    aiExplanation = null,
    isGeneratingAiExplanation = false,
    validationErrors = [],
    onAiExplain,
    onClose,
    aiModelOptions = [],
    selectedModel,
    onModelChange,
    onAutoFix,
    isAutoFixing = false,
    autoFixModel,
    autoFixModelOptions = [],
    onAutoFixModelChange,
    unfixableReason,
}) => {
    if (isExplaining) {
        return (
            <McCard style={{ marginTop: '16px', position: 'relative' }}>
                {onClose && (
                    <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
                        <McIcon
                            icon="times-circle"
                            size="20"
                            style={{ cursor: 'pointer', color: 'var(--mds-color-neutral-strong-text)' }}
                            onClick={onClose}
                            title="Close Explain Plan"
                        />
                    </div>
                )}
                <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                    <McLoadingIndicator fit="medium" />
                    <span className="mds-text--medium-bold">Generating Explain Plan...</span>
                </div>
            </McCard>
        );
    }

    if (!planLines || planLines.length === 0) {
        if (validationErrors.length > 0) {
            return (
                <McCard style={{ marginTop: '16px', borderLeft: '4px solid var(--mds-color-error-main-background, #d32f2f)' }}>
                    <div style={{ padding: '20px', position: 'relative' }}>
                        {onClose && (
                            <div style={{ position: 'absolute', top: '12px', right: '12px', zIndex: 10 }}>
                                <McButton
                                    variant="plain"
                                    appearance="secondary"
                                    fit="small"
                                    onClick={onClose}
                                    title="Close validation card"
                                >
                                    <McIcon icon="times-circle" />
                                </McButton>
                            </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                            <McIcon icon="times-circle" size="20" style={{ color: 'var(--mds-color-error-main-background)' }} />
                            <h3 className="mds-headline--xx-small-bold" style={{ margin: 0 }}>Validation Errors</h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {validationErrors.map((err, idx) => (
                                <McNotification key={idx} appearance="error" body={err} />
                            ))}
                        </div>
                        {onAutoFix && (
                            unfixableReason ? (
                                <div style={{ marginTop: 12, fontSize: '0.85em', color: 'var(--mds-color-neutral-weak-text, #6b6b6b)' }}>
                                    <McIcon icon="shield" style={{ fontSize: 14, marginRight: 4, verticalAlign: 'middle' }} />
                                    {unfixableReason}
                                </div>
                            ) : (
                                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <McButton
                                        variant="filled"
                                        appearance="secondary"
                                        fit="small"
                                        onClick={onAutoFix}
                                        disabled={isAutoFixing}
                                    >
                                        {isAutoFixing ? <McLoadingIndicator slot="prefix" fit="small" /> : <McIcon icon="wrench" slot="prefix" />}
                                        Try Auto Fix
                                    </McButton>
                                    {autoFixModelOptions.length > 0 && onAutoFixModelChange && (
                                        <div style={{ display: 'flex', gap: 0, borderRadius: 999, overflow: 'hidden', border: '1px solid var(--mds-color-neutral-passive-background, #d6d6d6)' }}>
                                            {autoFixModelOptions.map((opt) => (
                                                <button
                                                    key={opt.value}
                                                    onClick={() => onAutoFixModelChange(opt.value)}
                                                    style={{
                                                        padding: '4px 12px',
                                                        fontSize: '0.78rem',
                                                        fontWeight: autoFixModel === opt.value ? 600 : 400,
                                                        cursor: 'pointer',
                                                        border: 'none',
                                                        background: autoFixModel === opt.value ? 'var(--mds-color-interactive-enabled-background, #0073ab)' : 'transparent',
                                                        color: autoFixModel === opt.value ? '#fff' : 'var(--mds-color-neutral-strong-text, #141414)',
                                                        transition: 'background 0.15s, color 0.15s',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        )}
                    </div>
                </McCard>
            );
        }
        return null;
    }

    return (
        <McCard style={{ marginTop: '16px', borderLeft: '4px solid var(--mds-color-primary-main-background, #0077c5)' }}>
            <div style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <McIcon icon="stack" size="20" style={{ color: 'var(--mds-color-primary-main-background)' }} />
                        <h3 className="mds-headline--xx-small-bold" style={{ margin: 0 }}>Execution Plan</h3>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {onAiExplain && !aiExplanation && aiModelOptions.length > 0 && onModelChange && (
                            <div style={{
                                display: 'inline-flex',
                                borderRadius: 20,
                                border: '1px solid var(--mds-color-neutral-weak-decoration, #e0e0e0)',
                                overflow: 'hidden',
                            }}>
                                {aiModelOptions.map((opt) => {
                                    const isActive = String(opt.value) === String(selectedModel);
                                    return (
                                        <button
                                            key={opt.value}
                                            onClick={() => onModelChange(opt.value)}
                                            disabled={isGeneratingAiExplanation}
                                            style={{
                                                padding: '4px 12px',
                                                fontSize: 11,
                                                fontWeight: isActive ? 700 : 500,
                                                border: 'none',
                                                cursor: isGeneratingAiExplanation ? 'default' : 'pointer',
                                                backgroundColor: isActive
                                                    ? 'var(--mds-color-brand-moss-100, #00754a)'
                                                    : 'transparent',
                                                color: isActive
                                                    ? 'var(--mds-color-neutral-white, #fff)'
                                                    : 'var(--mds-color-neutral-strong-text, #1f2937)',
                                                transition: 'all 0.15s ease',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {onAiExplain && !aiExplanation && (
                            <McButton
                                variant="filled"
                                appearance="secondary"
                                fit="small"
                                onClick={onAiExplain}
                                disabled={isGeneratingAiExplanation}
                            >
                                {isGeneratingAiExplanation ? <McLoadingIndicator slot="prefix" fit="small" /> : <McIcon icon="star" slot="prefix" />}
                                AI Assist
                            </McButton>
                        )}
                        {onClose && (
                            <McButton
                                variant="plain"
                                appearance="secondary"
                                fit="small"
                                onClick={onClose}
                                title="Close Execution Plan"
                            >
                                <McIcon icon="times-circle" />
                            </McButton>
                        )}
                    </div>
                </div>

                <div style={{
                    backgroundColor: 'var(--mds-color-neutral-weakest-background, #f8f8f8)',
                    padding: '16px',
                    borderRadius: '8px',
                    overflowX: 'auto',
                    fontFamily: 'var(--mds-font-family-mono, monospace)',
                    fontSize: '13px',
                    lineHeight: '1.5',
                    color: 'var(--mds-color-neutral-strongest-text, #111)',
                    maxHeight: '400px',
                    border: '1px solid var(--mds-color-neutral-weak-decoration, #eee)'
                }}>
                    {planLines.map((line, idx) => (
                        <div key={idx} style={{ whiteSpace: 'pre' }}>
                            {line}
                        </div>
                    ))}
                </div>

                {isGeneratingAiExplanation && (
                    <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', borderRadius: '8px', backgroundColor: 'var(--mds-color-primary-weakest-background, #e3f2fd)' }}>
                        <McLoadingIndicator fit="small" />
                        <span className="mds-text--small-bold">AI is analyzing the execution plan...</span>
                    </div>
                )}

                {aiExplanation && (
                    <div style={{ marginTop: '16px' }}>
                        <McNotification
                            appearance="info"
                            heading="AI Plan Explanation"
                            style={{ width: '100%' }}
                        >
                            <div style={{
                                marginTop: '8px',
                                whiteSpace: 'pre-wrap',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                color: 'var(--mds-color-neutral-strong-text)'
                            }}>
                                {aiExplanation}
                            </div>
                        </McNotification>
                    </div>
                )}
            </div>
        </McCard>
    );
};
